import { Component, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { WarehouseEffects } from "../../State/WarehouseEffects";
import { WarehouseState } from "../../State/WarehouseState";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TablePagination,
  TableSearchEvent,
  TableSort,
  TableDateRangeEvent,
} from "../../../../Common/Components/Table/Type/TableType";
import { Warehouse } from "../../Dto/WarehouseModel";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-warehouse-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./WarehouseHomePage.html",
  styleUrl: "./WarehouseHomePage.scss",
})
export class WarehouseHomePage implements OnInit {
  private readonly warehouseEffects = inject(WarehouseEffects);
  readonly warehouseState = inject(WarehouseState);
  private readonly router = inject(Router);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);

  warehouseColumns: TableColumn<Warehouse>[] = [
    { key: "id", header: "ID", sortable: true, width: "10%", visibleByDefault: false },
    { key: "name", header: "Name", sortable: true, width: "20%" },
    {
      key: "address",
      header: "Address",
      sortable: false,
      width: "35%",
      cellTemplate: (item: Warehouse) =>
        `${item.address.stateProvinceRegion}, ${item.address.countyDistrict}, ${item.address.subdistrictMunicipality}, ${item.address.communeVillage}, ${item.address.line} ${item.address.zip}`,
    },
    {
      key: "note",
      header: "Note",
      sortable: false,
      width: "20%",
      cellTemplate: (item: Warehouse) => this.truncateText(this.stripHtml(item.note ?? "")),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "15%",
      cellTemplate: (item: Warehouse) => formatDateNumeric(item.createdAt),
    },
  ];

  get warehouseConfig(): TableConfig<Warehouse> {
    return {
      loading: this.warehouseState.loading(),
      emptyMessage: this.warehouseState.error(),
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search warehouses...",
      showCreate: true,
      createButtonLabel: "Create Warehouse",
      createButtonIcon: "add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      onCreate: () => this.createWarehouse(),
      actionButtons: [
        {
          label: "edit",
          icon: "edit",
          handler: (warehouse: Warehouse) => this.editWarehouse(warehouse),
        },
        {
          label: "delete",
          icon: "delete",
          handler: (warehouse: Warehouse) => this.deleteWarehouse(warehouse),
        },
      ],
    };
  }

  get warehousePagination(): TablePagination {
    return this.warehouseState.pagination();
  }

  ngOnInit(): void {
    this.warehouseEffects.searchWarehouses();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    return;
  }

  onPageChange(event: { page: number; search?: string }): void {
    const currentSearchParams = this.warehouseState.searchParams();
    this.warehouseEffects.searchWarehouses({ ...currentSearchParams, page: event.page, search: event.search });
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const currentSearchParams = this.warehouseState.searchParams();
    this.warehouseEffects.searchWarehouses({ ...currentSearchParams, page: 1, limit: event.limit, search: event.search });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    return;
  }

  onSearchChange(event: TableSearchEvent): void {
    this.warehouseEffects.searchWarehouses({ search: event.term, page: event.page });
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const currentSearchParams = this.warehouseState.searchParams();
    this.warehouseEffects.searchWarehouses({
      ...currentSearchParams,
      page: event.page,
      createdAtFrom: event.dateRange.from,
      createdAtTo: event.dateRange.to,
    });
  }

  createWarehouse(): void {
    this.router.navigate(["/warehouses/create"]);
  }

  editWarehouse(warehouse: Warehouse): void {
    this.router.navigate(["/warehouses/edit", warehouse.id]).catch(console.error);
  }

  deleteWarehouse(warehouse: Warehouse): void {
    this.confirmModalEffects.open({
      title: "Delete Warehouse",
      message: `Are you sure you want to delete the warehouse "${warehouse.name}"? This action cannot be undone.`,
      confirmText: "Delete Warehouse",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          await this.warehouseEffects.deleteWarehouse(warehouse.id);
          this.toastEffects.show(`Warehouse "${warehouse.name}" has been deleted.`, "success");
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, `Failed to delete warehouse "${warehouse.name}".`), "error");
        }
      },
    });
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  private truncateText(value: string, maxLength = 80): string {
    if (value.length <= maxLength) {
      return value;
    }

    return `${value.slice(0, maxLength)}...`;
  }
}
